/**
 * PlanRevealScreen
 *
 * Personalized plan reveal — shown after the "Building your plan" animation.
 * Surfaces key insights based on what the user just told us:
 * journey overview, testing, clinics, cost estimate, condition tips.
 */

import React, { useEffect, useRef, useMemo } from "react";
import { View, Text, ScrollView, Animated } from "react-native";
import {
  Route,
  TestTube,
  Building2,
  DollarSign,
  Heart,
  Sparkles,
  ClipboardCheck,
} from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations, useLocale, formatCurrency } from "@src/packages/i18n";
import { usePlanBuilder } from "../PlanBuilderContext";
import { getJourneyForState } from "@src/packages/homepage/plan/journeys";
import { resolveAcceptedPlanModality } from "@src/packages/homepage/plan/resolveAcceptedPlanModality";
import { useCostEstimate } from "@src/packages/homepage/plan/intelligence";
import { useLocalClinics } from "@src/packages/clinics/hooks/useLocalClinics";
import type { Pathway } from "@src/packages/homepage/plan/plan.types";
import type { HomepageGender } from "@src/packages/homepage/homepage.types";

type Props = {
  onDismiss: () => void;
};

/** Map builder partner status to journey pathway */
function toPathway(partnerStatus: string | null): Pathway {
  if (partnerStatus === "with-partner") return "with-partner";
  if (partnerStatus === "using-donor") return "with-donor";
  return "solo";
}

/** Friendly goal labels — translation keys, resolved at render */
const GOAL_KEYS: Record<string, string> = {
  preserve: "plan_builder.plan_reveal.goal.preserve",
  "embryo-preserve": "plan_builder.plan_reveal.goal.embryo_preserve",
  pregnancy: "plan_builder.plan_reveal.goal.pregnancy",
  "egg-donation": "plan_builder.plan_reveal.goal.egg_donation",
  "sperm-donation": "plan_builder.plan_reveal.goal.sperm_donation",
  adoption: "plan_builder.plan_reveal.goal.adoption",
  surrogacy: "plan_builder.plan_reveal.goal.surrogacy",
};

const ACCEPTED_PLAN_LABELS: Record<string, string> = {
  ivf: "plan_builder.accepted_plan.ivf_option",
  iui: "plan_builder.accepted_plan.iui_option",
  egg_freezing: "plan_builder.accepted_plan.egg_freezing_option",
  embryo_freezing: "plan_builder.accepted_plan.embryo_freezing_option",
  sperm_freezing: "plan_builder.accepted_plan.sperm_freezing_option",
  fet: "plan_builder.accepted_plan.fet_option",
  conventional_ivf: "plan_builder.accepted_plan.conventional_ivf_option",
  icsi: "plan_builder.accepted_plan.icsi_option",
  split_ivf_icsi: "plan_builder.accepted_plan.split_ivf_icsi_option",
  rescue_icsi: "plan_builder.accepted_plan.rescue_icsi_option",
  fresh_transfer: "plan_builder.accepted_plan.fresh_transfer_option",
  freeze_all: "plan_builder.accepted_plan.freeze_all_option",
  later_fet: "plan_builder.accepted_plan.later_fet_option",
  no_transfer: "plan_builder.accepted_plan.no_transfer_option",
  pgt_a: "plan_builder.accepted_plan.pgt_a_option",
  pgt_m: "plan_builder.accepted_plan.pgt_m_option",
  pgt_sr: "plan_builder.accepted_plan.pgt_sr_option",
  other: "plan_builder.accepted_plan.other_pgt_option",
  not_planned: "plan_builder.accepted_plan.not_planned_option",
  undecided: "plan_builder.accepted_plan.not_decided_option",
  not_applicable: "plan_builder.accepted_plan.not_applicable_option",
};

/** Look up a localized journey string, falling back to the English literal. */
function localizeJourneyText(
  t: ReturnType<typeof useTranslations>,
  journeyKey: string,
  field: string,
  fallback: string,
): string {
  const path = `journey.${journeyKey.replace(/-/g, "_")}.${field}`;
  const value = t(path);
  return value === path ? fallback : value;
}

/** Condition labels — translation keys, resolved at render */
const CONDITION_KEYS: Record<string, string> = {
  pcos: "plan_builder.plan_reveal.condition.pcos",
  endometriosis: "plan_builder.plan_reveal.condition.endometriosis",
  thyroid_disease: "plan_builder.plan_reveal.condition.thyroid_disease",
  diminished_ovarian_reserve:
    "plan_builder.plan_reveal.condition.diminished_ovarian_reserve",
  primary_ovarian_insufficiency:
    "plan_builder.plan_reveal.condition.primary_ovarian_insufficiency",
  fibroids: "plan_builder.plan_reveal.condition.fibroids",
  irregular: "plan_builder.plan_reveal.condition.irregular",
  loss: "plan_builder.plan_reveal.condition.loss",
  cancer: "plan_builder.plan_reveal.condition.cancer",
  "male-factor": "plan_builder.plan_reveal.condition.male_factor",
  other: "plan_builder.plan_reveal.condition.other",
};

/** Animated reveal card */
function RevealCard({
  delay,
  children,
}: {
  delay: number;
  children: React.ReactNode;
}) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(16)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 400,
          useNativeDriver: true,
        }),
        Animated.spring(translateY, {
          toValue: 0,
          damping: 16,
          stiffness: 120,
          useNativeDriver: true,
        }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, opacity, translateY]);

  return (
    <Animated.View style={{ opacity, transform: [{ translateY }] }}>
      {children}
    </Animated.View>
  );
}

export default function PlanRevealScreen({ onDismiss }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { state } = usePlanBuilder();

  // Resolve journey
  const gender: HomepageGender = state.gender ?? "female";
  const goal = state.goal ?? "preserve";
  const pathway = toPathway(state.partnerStatus);
  const selectedModality =
    state.stage === "trying-naturally"
      ? "natural"
      : resolveAcceptedPlanModality(state.acceptedPlan);
  const journey = useMemo(
    () => getJourneyForState(gender, goal, pathway, selectedModality),
    [gender, goal, pathway, selectedModality],
  );

  // Cost estimate
  const { estimate } = useCostEstimate(goal, gender, selectedModality);

  // Clinic count
  const { totalFiltered: clinicCount } = useLocalClinics({});

  // Condition names for display
  const conditionLabels = useMemo(() => {
    return state.conditions
      .filter((c) => c !== "none_or_unsure")
      .map((c) => (CONDITION_KEYS[c] ? t(CONDITION_KEYS[c]) : c));
  }, [state.conditions, t]);

  const hasConditions = conditionLabels.length > 0;
  const goalLabel = GOAL_KEYS[goal]
    ? t(GOAL_KEYS[goal])
    : t("plan_builder.plan_reveal.goal_journey_fallback");
  const stepCount = journey.steps.length;
  const acceptedPlanValue = (value: string) =>
    ACCEPTED_PLAN_LABELS[value] ? t(ACCEPTED_PLAN_LABELS[value]) : value;

  // Format cost (locale-aware: USD for en, EUR for fr)
  const costDisplay = estimate
    ? formatCurrency(Math.round(estimate.totalEstimatedCents / 100), locale)
    : null;
  const outOfPocketDisplay = estimate
    ? formatCurrency(Math.round(estimate.totalOutOfPocketCents / 100), locale)
    : null;

  // Hero animation
  const heroOpacity = useRef(new Animated.Value(0)).current;
  const heroScale = useRef(new Animated.Value(0.96)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(heroOpacity, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.spring(heroScale, {
        toValue: 1,
        damping: 14,
        stiffness: 120,
        useNativeDriver: true,
      }),
    ]).start();
  }, [heroOpacity, heroScale]);

  return (
    <ScrollView
      testID="plan-builder-reveal-screen"
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      {/* Hero */}
      <Animated.View
        style={{
          opacity: heroOpacity,
          transform: [{ scale: heroScale }],
          alignItems: "center",
          marginBottom: 32,
          marginTop: 16,
        }}
      >
        <View
          style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: `${EMBER.colors.warm}15`,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <Sparkles size={26} color={EMBER.colors.warm} />
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 24,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
            lineHeight: 30,
            marginBottom: 6,
          }}
        >
          {t("plan_builder.plan_reveal.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "400",
            color: EMBER.colors.textLight,
            textAlign: "center",
            lineHeight: 22,
          }}
        >
          {t("plan_builder.plan_reveal.subtitle")}
        </Text>
      </Animated.View>

      {/* Accepted clinic plan */}
      {state.acceptedPlanAcknowledged && (
        <RevealCard delay={150}>
          <View
            testID="plan-builder-accepted-plan-summary"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.82)",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.9)",
              padding: 20,
              marginBottom: 12,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.04,
              shadowRadius: 8,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <ClipboardCheck size={20} color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.text,
                  fontSize: 17,
                  fontWeight: "600",
                }}
              >
                {t("plan_builder.plan_reveal.accepted_plan_title")}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 13,
                lineHeight: 19,
                marginBottom: 14,
              }}
            >
              {t("plan_builder.plan_reveal.accepted_plan_subtitle")}
            </Text>
            <View style={{ gap: 8 }}>
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.text, fontSize: 14 }}
              >
                {t("plan_builder.plan_reveal.accepted_plan_treatment", {
                  params: {
                    value: acceptedPlanValue(
                      state.acceptedPlan.treatmentFamily,
                    ),
                  },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.text, fontSize: 14 }}
              >
                {t("plan_builder.plan_reveal.accepted_plan_fertilization", {
                  params: {
                    value: acceptedPlanValue(
                      state.acceptedPlan.fertilizationMethod,
                    ),
                  },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.text, fontSize: 14 }}
              >
                {t("plan_builder.plan_reveal.accepted_plan_transfer", {
                  params: {
                    value: acceptedPlanValue(state.acceptedPlan.transferPlan),
                  },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.text, fontSize: 14 }}
              >
                {t("plan_builder.plan_reveal.accepted_plan_genetic_testing", {
                  params: {
                    value: acceptedPlanValue(state.acceptedPlan.pgtPlan),
                  },
                })}
              </Text>
            </View>
          </View>
        </RevealCard>
      )}

      {/* Journey overview */}
      <RevealCard delay={200}>
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.9)",
            padding: 20,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <Route size={20} color={EMBER.colors.warm} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 17,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {goalLabel}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 20,
              marginBottom: 16,
            }}
          >
            {localizeJourneyText(
              t,
              journey.key,
              "description",
              journey.description,
            )}
          </Text>
          {/* Step list */}
          <View style={{ gap: 10 }}>
            {journey.steps.map((step, i) => (
              <View
                key={step.id}
                style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
              >
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor: `${EMBER.colors.text}10`,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      fontWeight: "700",
                      color: EMBER.colors.text,
                    }}
                  >
                    {i + 1}
                  </Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "500",
                      color: EMBER.colors.text,
                    }}
                  >
                    {localizeJourneyText(
                      t,
                      journey.key,
                      `${step.id}_title`,
                      step.title,
                    )}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "400",
                      color: EMBER.colors.textLight,
                    }}
                  >
                    {localizeJourneyText(
                      t,
                      journey.key,
                      `${step.id}_subtitle`,
                      step.subtitle,
                    )}
                  </Text>
                </View>
              </View>
            ))}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.textMid,
              marginTop: 14,
            }}
          >
            {t("plan_builder.plan_reveal.journey_step_count", {
              params: { count: String(stepCount) },
            })}
          </Text>
        </View>
      </RevealCard>

      {/* Testing */}
      <RevealCard delay={400}>
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.9)",
            padding: 20,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
            }}
          >
            <TestTube size={20} color={EMBER.colors.warm} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 17,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {t("plan_builder.plan_reveal.testing_title")}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 20,
            }}
          >
            {state.knownResults.hasAMH ||
            state.knownResults.hasFSH ||
            state.knownResults.hasSA
              ? t("plan_builder.plan_reveal.testing_have_results")
              : t("plan_builder.plan_reveal.testing_no_results")}
          </Text>
        </View>
      </RevealCard>

      {/* Clinics */}
      {clinicCount > 0 && (
        <RevealCard delay={550}>
          <View
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.82)",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.9)",
              padding: 20,
              marginBottom: 12,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.04,
              shadowRadius: 8,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <Building2 size={20} color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                }}
              >
                {t("plan_builder.plan_reveal.clinics_title", {
                  params: { count: String(clinicCount) },
                })}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "400",
                color: EMBER.colors.textLight,
                lineHeight: 20,
              }}
            >
              {t("plan_builder.plan_reveal.clinics_body")}
              {state.zipCode
                ? t("plan_builder.plan_reveal.clinics_distance_suffix")
                : ""}
            </Text>
          </View>
        </RevealCard>
      )}

      {/* Cost estimate */}
      {costDisplay && (
        <RevealCard delay={700}>
          <View
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.82)",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.9)",
              padding: 20,
              marginBottom: 12,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.04,
              shadowRadius: 8,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <DollarSign size={20} color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                }}
              >
                {t("plan_builder.plan_reveal.cost_title")}
              </Text>
            </View>
            <View style={{ flexDirection: "row", gap: 16, marginBottom: 8 }}>
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 22,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  {estimate!.coverageStatus !== "none"
                    ? outOfPocketDisplay
                    : costDisplay}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "400",
                    color: EMBER.colors.textLight,
                  }}
                >
                  {estimate!.coverageStatus !== "none"
                    ? t("plan_builder.plan_reveal.cost_out_of_pocket")
                    : t("plan_builder.plan_reveal.cost_total")}
                </Text>
              </View>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "400",
                color: EMBER.colors.textLight,
                lineHeight: 19,
              }}
            >
              {state.hasInsurance
                ? t("plan_builder.plan_reveal.cost_with_insurance")
                : t("plan_builder.plan_reveal.cost_without_insurance")}
            </Text>
          </View>
        </RevealCard>
      )}

      {/* Conditions */}
      {hasConditions && (
        <RevealCard delay={850}>
          <View
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.82)",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.9)",
              padding: 20,
              marginBottom: 12,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.04,
              shadowRadius: 8,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <Heart size={20} color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                }}
              >
                {t("plan_builder.plan_reveal.conditions_title")}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "400",
                color: EMBER.colors.textLight,
                lineHeight: 20,
                marginBottom: 10,
              }}
            >
              {t("plan_builder.plan_reveal.conditions_body_prefix")}
              <Text style={{ fontWeight: "600", color: EMBER.colors.textMid }}>
                {conditionLabels.join(", ")}
              </Text>
              {t("plan_builder.plan_reveal.conditions_body_suffix")}
            </Text>
          </View>
        </RevealCard>
      )}

      {/* CTA */}
      <RevealCard delay={hasConditions ? 1000 : 850}>
        <View style={{ marginTop: 12 }}>
          <WarmButton
            title={t("plan_builder.plan_reveal.cta_go_to_plan")}
            onPress={onDismiss}
          />
        </View>
      </RevealCard>
    </ScrollView>
  );
}
